// Angular imports
import { Injector } from '@angular/core';

// 3rd party imports
import { Observable } from 'rxjs';

// devextreme imports
import notify from 'devextreme/ui/notify';

// cc-common imports
import { IconConstants, Utils } from 'cc-common';

// Constant imports
import { MessageInterval } from '../../_constants/messages-intervals';
import { StringFormatConstants } from '../../_constants/string-format-constants';
import { BoardApprovalActionConstants } from '../../shared-acc-board-approval-module/_constants/board-approvals-action-constants';

// Service imports
import { UserService } from '../../_services/data-services/user.service';
import { CiraHelpService } from '../../_services/shared-services/cira-help.service';
import { HelperService } from '../../_services/shared-services/helper.service';
import { PrecisionService } from '../../_services/shared-services/precision.service';
import { ScreenService } from '../../_services/shared-services/screen.service';
import { ServerErrorService } from '../../_services/shared-services/server-error.service';
import { BoardApprovalHelperService } from '../_services/board-approval-helper.service';
import { BoardApprovalService } from '../_services/board-approval.service';
import { SharedBoardApprovalService } from '../../shared-acc-board-approval-module/_services/shared-acc-board-approval.service';

// model imports
import { BoardApprovalParameterDto } from '../../shared-acc-board-approval-module/_models/board-approval-parameter-dto';
import { BoardApprovalProcessesResultDto } from '../../shared-acc-board-approval-module/_models/board-approval-processes-result-dto';
import { BoardApprovalQueueParameterDto } from '../../shared-acc-board-approval-module/_models/board-approval-queue-parameter-dto';
import { BoardApprovalsActionParametersDto } from '../../shared-acc-board-approval-module/_models/board-approvals-action-parameters-dto';

export abstract class BoardApprovalBaseComponent {
    protected userService: UserService;
    protected helperService: HelperService;
    protected screenService: ScreenService;
    protected errorService: ServerErrorService;
    protected precisionService: PrecisionService;
    protected boardApprovalHelperService: BoardApprovalHelperService;
    protected boardApprovalService: BoardApprovalService;
    protected ciraHelpService: CiraHelpService;
    protected sharedBoardApprovalService: SharedBoardApprovalService;

    isSmallScreen = false;
    currencyFormat: string;
    dateFormat: string;

    processResult: Array<BoardApprovalProcessesResultDto> = [];
    selectedProcess: string;
    queueParams: BoardApprovalQueueParameterDto;

    recordsToProcess: Array<BoardApprovalParameterDto> = [];

    iconHelp = IconConstants.HELP;
    iconEye = IconConstants.VIEW;
    iconEyeBlue = `${IconConstants.VIEW} dx-theme-accent-as-text-color`;
    iconRunner = IconConstants.RUNNER;
    iconRunnerBlue = `${IconConstants.RUNNER} dx-theme-accent-as-text-color`;
    iconAttachment = IconConstants.ATTACHMENT;
    iconNotes = IconConstants.NOTES_AVAILABLE;
    iconEmptyNotes = IconConstants.NOTES_EMPTY;
    iconHistory = IconConstants.HISTORY;
    iconCheck = IconConstants.CHECK;
    iconCross = IconConstants.CROSS;
    iconInfo = IconConstants.INFO;
    iconFlag = IconConstants.FLAG;
    iconWarn = IconConstants.WARN;
    iconRefresh = IconConstants.REFRESH;
    iconCancel = IconConstants.CLOSE;
    iconSave = IconConstants.SAVE;

    isInternalUser: boolean;

    constructor(protected readonly injector: Injector) {
        this.userService = injector.get(UserService);
        this.helperService = injector.get(HelperService);
        this.screenService = injector.get(ScreenService);
        this.errorService = injector.get(ServerErrorService);
        this.precisionService = injector.get(PrecisionService);
        this.boardApprovalHelperService = injector.get(BoardApprovalHelperService);
        this.boardApprovalService = injector.get(BoardApprovalService);
        this.ciraHelpService = injector.get(CiraHelpService);
        this.sharedBoardApprovalService = injector.get(SharedBoardApprovalService);

        this.updateScreenSize();

        this.dateFormat = StringFormatConstants.dateFormat;
        this.currencyFormat = this.precisionService.getCurrencyFormat();
        this.isInternalUser = this.userService.isInternal();
    }

    onHelpKeyClick(processCode: string, communityId: number): void {
        const helpKey = this.processResult.find((d) =>
            Utils.equalsIgnoreCase(d.processCode, processCode)
        ).processHelpKey;
        this.showCiraHelp(helpKey, communityId);
    }

    showCiraHelp(helpKey: string, communityId: number): void {
        const helpLink = this.ciraHelpService.getHelpLinkByKey(helpKey, communityId);
        this.ciraHelpService.showHelp(helpLink);
    }

    saveAccAprrovals(actionTitle: string, param: BoardApprovalsActionParametersDto): void {
        let service: Observable<any>;
        switch (actionTitle) {
            case BoardApprovalActionConstants.APPROVE_TITLE:
                service = this.boardApprovalService.approveMultipleBoardApprovalAcc(param);
                break;
            case BoardApprovalActionConstants.APPROVE_WITH_STIPULATION_TITLE:
                service = this.boardApprovalService.approveMultipleWithStipulationBoardApprovalAcc(param);
                break;
            case BoardApprovalActionConstants.DENY_ADDITIONAL_INFORMATION_TITLE:
                service = this.boardApprovalService.denyMultipleBoardApprovalAcc(param);
                break;
            case BoardApprovalActionConstants.IN_REVIEW_TITLE:
                service = this.boardApprovalService.holdMultipleBoardApprovalAcc(param);
                break;
        }

        if (Utils.notNullAndDefined(service)) {
            this.saveMultipleApprovals(actionTitle, service);
        }
    }

    saveWFApprovals(actionTitle: string, param: BoardApprovalsActionParametersDto): void {
        let service: Observable<any>;
        switch (actionTitle) {
            case BoardApprovalActionConstants.APPROVE_TITLE:
                service = this.boardApprovalService.approveMultipleBoardApprovalWf(param);
                break;
            case BoardApprovalActionConstants.APPROVE_WITH_STIPULATION_TITLE:
                service = this.boardApprovalService.approveMultipleWithStipulationBoardApprovalWf(param);
                break;
            case BoardApprovalActionConstants.DENY_ADDITIONAL_INFORMATION_TITLE:
                service = this.boardApprovalService.denyMultipleBoardApprovalWf(param);
                break;
            case BoardApprovalActionConstants.IN_REVIEW_TITLE:
                service = this.boardApprovalService.holdMultipleBoardApprovalWf(param);
                break;
        }
        if (Utils.notNullAndDefined(service)) {
            this.saveMultipleApprovals(actionTitle, service);
        }
    }

    saveCollectionApprovals(actionTitle: string, param: BoardApprovalsActionParametersDto): void {
        let service: Observable<any>;
        switch (actionTitle) {
            case BoardApprovalActionConstants.APPROVE_TITLE:
                service = this.boardApprovalService.approveMultipleBoardApprovalCollection(param);
                break;
            case BoardApprovalActionConstants.APPROVE_WITH_STIPULATION_TITLE:
                service = this.boardApprovalService.approveMultipleWithStipulationBoardApprovalCollection(param);
                break;
            case BoardApprovalActionConstants.DENY_ADDITIONAL_INFORMATION_TITLE:
                service = this.boardApprovalService.denyMultipleBoardApprovalCollection(param);
                break;
            case BoardApprovalActionConstants.IN_REVIEW_TITLE:
                service = this.boardApprovalService.holdMultipleBoardApprovalCollection(param);
                break;
        }
        if (Utils.notNullAndDefined(service)) {
            this.saveMultipleApprovals(actionTitle, service);
        }
    }

    private saveMultipleApprovals(actionTitle: string, service: any): void {
        this.boardApprovalHelperService.showLoading();
        service.subscribe(
            () => {
                notify(
                    `Multiple ${actionTitle} for ${this.selectedProcess} records completed successfully.`,
                    'Success',
                    MessageInterval.successMsgInterval
                );
                this.boardApprovalHelperService.hideLoading();
                setTimeout(() => {
                    this.boardApprovalService.updateBoardApprovalRecords();
                }, 100);
            },
            (error) => {
                this.errorService.displayError(error);
                this.boardApprovalHelperService.hideLoading();
            }
        );
    }

    private updateScreenSize(): void {
        this.isSmallScreen = this.screenService.isSmallScreen();
        this.screenService.changed.subscribe(() => (this.isSmallScreen = this.screenService.isSmallScreen()));
    }
}
